// ENG-5499 / ENG-5515 (Alerts paging) — snooze duration parsing.
//
// Canonical, framework-agnostic so both the team-scoped Hono API
// (packages/api/src/routes/alerts.ts) and the admin Mission Control endpoints
// (webapp) resolve a snooze token to the same absolute timestamp. The paging
// worker (alert-pager.ts) gates fresh pages on the snoozed_until this produces.

export type SnoozeDuration = '15m' | '1h' | '4h' | 'until_tomorrow';

export const SNOOZE_DURATIONS: readonly SnoozeDuration[] = [
  '15m',
  '1h',
  '4h',
  'until_tomorrow',
] as const;

const FIXED_SECONDS: Record<Exclude<SnoozeDuration, 'until_tomorrow'>, number> = {
  '15m': 15 * 60,
  '1h': 60 * 60,
  '4h': 4 * 60 * 60,
};

/**
 * Resolve a snooze token to an absolute ISO timestamp. Returns null for an
 * unknown token so the caller can 400 rather than silently snoozing forever.
 *
 * `until_tomorrow` = 09:00 UTC the next calendar day — a stable "deal with it
 * in the morning" target. Team-timezone-aware until-tomorrow is a post-v1
 * refinement; UTC keeps it unambiguous.
 */
export function computeSnoozeUntil(duration: string, now: Date = new Date()): string | null {
  if (duration === 'until_tomorrow') {
    const t = new Date(now);
    t.setUTCDate(t.getUTCDate() + 1);
    t.setUTCHours(9, 0, 0, 0);
    return t.toISOString();
  }
  if (duration in FIXED_SECONDS) {
    const seconds = FIXED_SECONDS[duration as keyof typeof FIXED_SECONDS];
    return new Date(now.getTime() + seconds * 1000).toISOString();
  }
  return null;
}
